import { api } from "@/lib/api";

export const CHUNK = 5 * 1024 * 1024; // 5MB pieces — small enough that no single request hits the proxy timeout
const UP = { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 };
const COMPLETE = { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 };   // finalise = assemble + one big storage write
const LEASE_S = 420;   // backend `completing` lease (COMPLETING_LEASE_S): a stale lease is reclaimable only after this — recovery must outlive it
const TERMINAL_409 = new Set(["duplicate", "duplicate_terminal", "restart_required"]);   // the ONLY 409s that make a session unresumable
const MAX_ATTEMPTS = 5;
const SESSIONS_KEY = "bf_upload_sessions";   // open chunked sessions, so a page/browser restart can Resume instead of re-sending everything

export const newTraceId = () => (crypto?.randomUUID ? crypto.randomUUID().replace(/-/g, "").slice(0, 12) : Math.random().toString(36).slice(2, 14));
export const fileKey = (file) => `${file.name}|${file.size}|${file.lastModified}`;

// ---- error model: the dialog shows the exact stage/chunk/trace instead of a generic "failed"
export class UploadError extends Error {
  constructor({ stage, message, status, traceId, uploadId, chunkIndex, totalChunks, network, code, detail, resumable }) {
    super(message);
    Object.assign(this, { stage, status, traceId, uploadId, chunkIndex, totalChunks, network: !!network, code, detail, resumable: !!resumable });
  }
}
const isNetwork = (e) => !e?.response;                                  // no HTTP response at all: proxy/network/aborted/timeout
const isRetryable = (e) => isNetwork(e) || (e.response.status >= 500);   // never retry 4xx validation/auth/duplicate
const detailOf = (e) => e?.response?.data?.detail;
const messageOf = (e) => {
  const d = detailOf(e);
  if (d && typeof d === "object") return d.message || d.code || "upload error";
  if (typeof d === "string") return d;
  if (isNetwork(e)) return e?.code === "ECONNABORTED" ? "request timed out" : "network error (no response from server — proxy or connection interrupted)";
  return `HTTP ${e?.response?.status}`;
};
const wrap = (e, stage, ctx) => {
  const d = detailOf(e);
  return new UploadError({ stage, message: messageOf(e), status: e?.response?.status, network: isNetwork(e),
    traceId: (d && typeof d === "object" && d.trace_id) || ctx.traceId, uploadId: ctx.uploadId, chunkIndex: ctx.chunkIndex, totalChunks: ctx.totalChunks,
    code: d && typeof d === "object" ? d.code : undefined, detail: d, resumable: ctx.resumable });
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// retry network/5xx with exponential backoff (0.5s, 1s, 2s, 4s); 4xx surfaces immediately
async function withRetries(fn, onAttempt) {
  let last;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try { return await fn(); }
    catch (e) {
      last = e;
      if (!isRetryable(e) || attempt === MAX_ATTEMPTS) throw e;
      onAttempt?.(attempt, e);
      await sleep(Math.min(8000, 500 * 2 ** (attempt - 1)));
    }
  }
  throw last;
}

// ---- persisted open sessions (localStorage): { [fileKey]: { uploadId, traceId, size, name, at } }
const readSessions = () => { try { return JSON.parse(localStorage.getItem(SESSIONS_KEY) || "{}"); } catch { return {}; } };
const writeSessions = (m) => { try { localStorage.setItem(SESSIONS_KEY, JSON.stringify(m)); } catch {} };
export const rememberSession = (file, s) => writeSessions({ ...readSessions(), [fileKey(file)]: { ...s, name: file.name, size: file.size, at: Date.now() } });
export const forgetSession = (file) => { const m = readSessions(); delete m[fileKey(file)]; writeSessions(m); };
export const savedSessionFor = (file) => {
  const s = readSessions()[fileKey(file)];
  return s && Date.now() - s.at < 20 * 3600 * 1000 ? s : null;   // server purges sessions after 24h
};
// server view of a saved session: received/missing chunk indexes (null if it no longer exists)
export async function sessionStatus(uploadId) {
  try { const { data } = await api.get(`/media/upload/${uploadId}/status`); return data; }
  catch (e) { if (e?.response?.status === 404) return null; throw e; }
}

// ONE upload path for every surface (Add Content, Upload dialog, "Upload here"): small files go straight to POST /media, large ones are
// chunked through /media/upload/init -> /chunk -> /complete. `opts.resume = { uploadId, traceId }` continues an existing session and sends
// ONLY the chunks the server does not have yet. `opts.onStage(stageText)` reports progress text; `onProgress(pct)` a percentage.
export async function uploadMediaFile(file, fields = {}, onProgress, opts = {}) {
  const put = (form) => { Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null) form.append(k, String(v)); }); };
  const onStage = opts.onStage || (() => {});

  if (file.size <= CHUNK) {
    // ONE stable trace for the WHOLE single-shot retry sequence (automatic retries AND a later "Retry upload" click): the backend uses
    // it to replay a lost success response idempotently instead of reporting a duplicate.
    const traceId = opts.traceId || newTraceId();
    const form = new FormData();
    form.append("file", file);
    form.append("trace_id", traceId);
    put(form);
    onStage("Uploading…");
    try {
      const res = await withRetries(() => api.post("/media", form, UP), (n) => onStage(`Upload interrupted — retrying (${n}/${MAX_ATTEMPTS - 1})…`));
      onProgress?.(100);
      return res.data;
    } catch (e) { throw wrap(e, "UPLOAD", { traceId }); }
  }

  // ---- chunked
  const total = Math.ceil(file.size / CHUNK);
  let uploadId = opts.resume?.uploadId, traceId = opts.resume?.traceId, have = new Set();
  if (uploadId) {
    const st = await sessionStatus(uploadId);
    if (st && st.status === "completed" && st.media_id) {
      // already finalised earlier (e.g. the response was lost): /complete is idempotent and returns that media — no duplicate
      have = new Set(st.received_chunks || []); traceId = st.trace_id || traceId;
    } else if (st && (st.status === "open" || st.status === "completing")) {
      have = new Set(st.received_chunks || []); traceId = st.trace_id || traceId;
    } else {
      uploadId = null;   // expired/unknown: start a fresh session
    }
  }
  if (!uploadId) {
    // INIT → the backend upload_id is the ONLY id used from here on (never a locally generated preview id).
    let init;
    try {
      init = (await withRetries(() => api.post("/media/upload/init", { filename: file.name, size: file.size, trace_id: newTraceId() }))).data;
    } catch (e) { throw wrap(e, "INIT", {}); }
    if (!init?.upload_id) throw new UploadError({ stage: "INIT", message: "Upload init failed — no upload_id returned" });
    uploadId = init.upload_id; traceId = init.trace_id; have = new Set();
    rememberSession(file, { uploadId, traceId });
  }
  const ctx = { traceId, uploadId, totalChunks: total, resumable: true };
  const pending = [...Array(total).keys()].filter((c) => !have.has(c));
  let done = total - pending.length;
  onProgress?.(Math.round((done / total) * 100));
  for (const c of pending) {
    const cf = new FormData();
    cf.append("upload_id", uploadId);
    cf.append("index", String(c));
    cf.append("chunk", file.slice(c * CHUNK, (c + 1) * CHUNK), "chunk");
    onStage(`Uploading part ${c + 1} of ${total}…`);
    try {
      await withRetries(() => api.post("/media/upload/chunk", cf, UP), (n) => onStage(`Part ${c + 1}/${total} interrupted — retrying (${n}/${MAX_ATTEMPTS - 1})…`));
    } catch (e) {
      if (e?.response?.status === 404) forgetSession(file);   // session gone: a Resume must start over
      throw wrap(e, "CHUNK", { ...ctx, chunkIndex: c });
    }
    done += 1;
    onProgress?.(Math.round((done / total) * 100));
  }
  const doneForm = new FormData();
  doneForm.append("upload_id", uploadId);
  doneForm.append("filename", file.name);
  doneForm.append("content_type", file.type || "");
  put(doneForm);
  onStage("All parts received — finalising (assembling + saving the original)…");
  const complete = () => withRetries(() => api.post("/media/upload/complete", doneForm, COMPLETE), (n) => onStage(`Finalise interrupted — retrying (${n}/${MAX_ATTEMPTS - 1})…`));
  // Recovery window = the server lease + margin, so a stale `completing` lease can always be reclaimed before we give up.
  let deadline = Date.now() + (LEASE_S + 60) * 1000;
  for (;;) {
    try {
      const res = await complete();
      forgetSession(file);
      return res.data;
    } catch (e) {
      const status = e?.response?.status;
      const detail = detailOf(e) || {};
      const code = detail.code;
      if (status === 409 && code === "in_progress") {
        // another finaliser holds a FRESH lease: keep the session, wait at least as long as the server says the lease has left
        const retryAfterMs = Math.max(5, Number(detail.retry_after_s) || 0) * 1000;
        deadline = Math.max(deadline, Date.now() + retryAfterMs + 30000);
        if (Date.now() > deadline) throw wrap(e, "COMPLETE", ctx);
        onStage(`Finalisation already in progress on the server — waiting (~${Math.ceil(retryAfterMs / 1000)} s)…`);
        const st = await waitForFinalise(uploadId, deadline, onStage);
        if (st === "gone") { forgetSession(file); throw wrap(e, "COMPLETE", { ...ctx, resumable: false }); }
        continue;   // completed → idempotent /complete returns the existing media · open/stale lease → /complete finalises (backend reclaims)
      }
      // Forget the saved session ONLY on explicit terminal outcomes (exact duplicate / unknown session). Any other error keeps it resumable.
      if ((status === 409 && (TERMINAL_409.has(code) || !!detail.existing_id)) || status === 404) forgetSession(file);
      throw wrap(e, "COMPLETE", ctx);
    }
  }
}

// poll owner-scoped status every 5 s until the session leaves `completing` (or the lease can be reclaimed) — never past the deadline
async function waitForFinalise(uploadId, deadline, onStage) {
  while (Date.now() < deadline) {
    await sleep(5000);
    const st = await sessionStatus(uploadId);
    if (!st) return "gone";
    if (st.status === "completed") return "completed";
    if (st.status === "open") return "open";
    onStage("Finalisation already in progress on the server — waiting…");
  }
  return "timeout";   // lease is stale by now → the next /complete reclaims it atomically
}

// Human text for the dialog / toast: exact stage, chunk, HTTP status and trace id.
export const describeUploadError = (e) => {
  if (!(e instanceof UploadError)) return e?.message || "upload error";
  const where = e.stage === "CHUNK" ? `part ${e.chunkIndex + 1}/${e.totalChunks}` : e.stage === "COMPLETE" ? "finalise (all parts received)" : e.stage === "INIT" ? "init" : "upload";
  const kind = e.network ? "network/proxy failure — no response from the server" : `HTTP ${e.status}${e.code ? ` ${e.code}` : ""}`;
  return `Upload failed at ${where} · ${kind} · ${e.message}${e.traceId ? ` · Trace ${e.traceId}` : ""}`;
};
